import "./interface.scss";

import { delay, isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { Popover, PopoverBody } from "reactstrap";

import uniqueId from "../../utils/uniqueId";

// Keep track of any Tooltips that have been visible recently
// This means that if the user quickly jumps from one to the other
// we won't invoke the same delay that we normally invoke but will
// show it instantly instead
const REMOVE_RECENT_DELAY = 250;
const HOVER_TRIGGER_DELAY = 500;

let recentTooltips = {};

const addRecentTooltip = id => {
  recentTooltips[id] = true;
};

const removeRecentTooltip = id => {
  delete recentTooltips[id];
};

// const debouncedRemoveRecentTooltip = debounce(removeRecentTooltip, REMOVE_RECENT_DELAY);

// Should we show the tooltip immediately? Yes if we had another one open recently
const showImmediately = () => !isEmpty(recentTooltips);

// Work out an appropriate offset based on the tooltip placement
// const offsetForPlacement = placement => {
//   return placement.includes("bottom") || placement.includes("top") ? "0, 10" : "0";
// };

class Tooltip extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { isPopoverOpen: false, mustClickToClose: false };
    this.showTooltip = this.showTooltip.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
    this.toggleTooltip = this.toggleTooltip.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this._targetId = React.Children.only(this.props.children).props.id || `tooltip-${uniqueId()}`;
  }

  componentDidMount() {
    const targetNode = document.getElementById(this._targetId);
    //#!if iOS !== true
    if (this.props.triggerOnHover) {
      targetNode.addEventListener("mouseenter", this.onMouseEnter, false);
      targetNode.addEventListener("mouseleave", this.onMouseLeave, false);
    }
    //#!endif
    if (this.props.triggerOnClick) {
      targetNode.addEventListener("click", this.onClick, false);
    }

    // Hide tooltips when the user scrolls
    window.addEventListener("scroll", this.onScroll);
  }

  componentWillUnmount() {
    const targetNode = document.getElementById(this._targetId);
    if (targetNode) {
      //#!if iOS !== true
      targetNode.removeEventListener("mouseenter", this.onMouseEnter);
      targetNode.removeEventListener("mouseleave", this.onMouseLeave);
      //#!endif
      targetNode.removeEventListener("click", this.onClick, false);
    }

    window.removeEventListener("scroll", this.onScroll);

    if (this._delayShowTimerId) {
      clearTimeout(this._delayShowTimerId);
      this._delayShowTimerId = null;
    }
    if (this._removeRecentTimerId) {
      clearTimeout(this._removeRecentTimerId);
      this._removeRecentTimerId = null;
    }
  }

  showTooltip(mustClickToClose) {
    this.setState({ isPopoverOpen: true, mustClickToClose: Boolean(mustClickToClose) });
    addRecentTooltip(this._targetId);
    if (this._removeRecentTimerId) {
      clearTimeout(this._removeRecentTimerId);
      this._removeRecentTimerId = null;
    }
  }

  hideTooltip() {
    if (this._delayShowTimerId) {
      clearTimeout(this._delayShowTimerId);
      this._delayShowTimerId = null;
    }
    this.setState({ isPopoverOpen: false, mustClickToClose: false });
    this._removeRecentTimerId = delay(() => removeRecentTooltip(this._targetId), REMOVE_RECENT_DELAY);
  }

  toggleTooltip(e) {
    e.stopPropagation();
    this.setState({ isPopoverOpen: !this.state.isPopoverOpen, mustClickToClose: false });
  }

  onMouseEnter() {
    if (showImmediately()) {
      this.showTooltip(false);
    } else {
      const wait = this.props.hoverDelay ? this.props.hoverDelay : HOVER_TRIGGER_DELAY;
      this._delayShowTimerId = delay(this.showTooltip, wait);
    }
  }

  onMouseLeave() {
    if (!this.state.mustClickToClose) {
      this.hideTooltip();
    }
  }

  onScroll() {
    this.hideTooltip();
  }

  onClick(e) {
    e.stopPropagation();
    this.showTooltip(true);
  }

  renderPopover() {
    const placement = this.props.placement ? this.props.placement : "bottom-start";
    const offset = this.props.offset ? this.props.offset : "0"; //offsetForPlacement(placement);

    return (
      <Fragment>
        {this.state.isPopoverOpen && this.state.mustClickToClose ? (
          <div className="genericTooltipPopoverBackground" onClick={this.toggleTooltip} />
        ) : null}
        <Popover
          className="genericTooltipPopover"
          target={this._targetId}
          placement={placement}
          offset={offset}
          hideArrow={true}
          trigger="legacy"
          boundariesElement="viewport"
          isOpen={this.state.isPopoverOpen}
        >
          <PopoverBody>
            <div className="genericTooltip">{this.props.text}</div>
          </PopoverBody>
        </Popover>
      </Fragment>
    );
  }

  render() {
    const cloneItem = React.cloneElement(this.props.children, {
      id: this._targetId
    });

    return (
      <Fragment>
        {cloneItem}
        {this.renderPopover()}
      </Fragment>
    );
  }
}

Tooltip.defaultProps = {
  triggerOnHover: true,
  triggerOnClick: false,
  hoverDelay: HOVER_TRIGGER_DELAY
};

Tooltip.propTypes = {
  children: PropTypes.element.isRequired,
  text: PropTypes.string.isRequired,
  placement: PropTypes.string,
  offset: PropTypes.string,
  triggerOnClick: PropTypes.bool,
  triggerOnHover: PropTypes.bool,
  hoverDelay: PropTypes.number
};

export default Tooltip;

export const withOptionalTooltip = (children, tooltip, options) => {
  if (tooltip) {
    return (
      <Tooltip text={tooltip} {...options}>
        {children}
      </Tooltip>
    );
  }
  return children;
};
